/**
 * Utility functions for sanitizing user input to prevent XSS attacks
 */

/**
 * Sanitizes HTML content
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string
 */
export const sanitizeHtml = (html: string): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }
  return html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Sanitizes text content by encoding HTML entities
 * @param text - The text string to sanitize
 * @returns Sanitized text string
 */
export const sanitizeText = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Validates if a string is a safe URL
 * @param url - The URL to validate
 * @returns True if the URL is safe, false otherwise
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);
    // Allow http, https, data, and blob URLs
    return ['http:', 'https:', 'data:', 'blob:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
};

/**
 * Sanitizes a URL for safe use in src attributes
 * @param url - The URL to sanitize
 * @returns Sanitized URL or empty string if unsafe
 */
export const sanitizeUrl = (url: string): string => {
  if (!isValidUrl(url)) {
    return '';
  }

  return url;
};

/**
 * Sanitizes an image URL for safe use in img src attributes
 * @param url - The image URL to sanitize
 * @returns Sanitized image URL or empty string if unsafe
 */
export const sanitizeImageUrl = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Check if it's a valid URL
  if (!isValidUrl(url)) {
    return '';
  }

  // For data URLs, ensure they're image data URLs
  if (url.startsWith('data:') && !url.startsWith('data:image/')) {
    return '';
  }

  return url;
};

/**
 * Sanitizes a client identifier from URL or user input.
 * Only allows alphanumeric characters, hyphens, underscores, ampersands, and exclamation marks.
 * @param clientId - The client identifier to sanitize
 * @returns Sanitized client identifier or empty string if unsafe
 */
export const sanitizeClientId = (clientId: string): string => {
  if (!clientId || typeof clientId !== 'string') {
    return '';
  }

  const trimmed = clientId.trim();
  const safeClientIdPattern = /^[a-zA-Z0-9_&!-]+$/;

  return safeClientIdPattern.test(trimmed) ? trimmed : '';
};

/**
 * Sanitizes a username from URL route parameters or user input.
 * Allows alphanumeric characters, Auth0 database username symbols, and pipe (for Auth0 sub IDs).
 * @param username - The username to sanitize
 * @returns Sanitized username or empty string if unsafe
 */
export const sanitizeUsername = (username: string): string => {
  if (!username || typeof username !== 'string') {
    return '';
  }

  const trimmed = username.trim();
  const safeUsernamePattern = /^[a-zA-Z0-9._@|!#$'^`~+-]+$/;

  return safeUsernamePattern.test(trimmed) ? trimmed : '';
};

/**
 * Sanitizes an opaque API resource identifier from URL route parameters.
 * @param resourceId - The resource identifier to sanitize
 * @returns Sanitized resource identifier or empty string if unsafe
 */
export const sanitizeResourceId = (resourceId: string): string => {
  if (!resourceId || typeof resourceId !== 'string') {
    return '';
  }

  const trimmed = resourceId.trim();
  const safeResourceIdPattern = /^[a-zA-Z0-9._-]+$/;

  return safeResourceIdPattern.test(trimmed) ? trimmed : '';
};

/**
 * Sanitizes a route slug such as a tab name from URL route parameters.
 * @param slug - The route slug to sanitize
 * @returns Sanitized slug or empty string if unsafe
 */
export const sanitizeRouteSlug = sanitizeResourceId;

/**
 * Sanitizes a device type route parameter.
 * @param deviceType - The device type to sanitize
 * @returns Sanitized device type or empty string if unsafe
 */
export const sanitizeDeviceType = (deviceType: string): string => {
  if (deviceType === 'sensor' || deviceType === 'controller') {
    return deviceType;
  }
  return '';
};

/**
 * Sanitizes an invitation or reset token from URL route parameters.
 * @param token - The token to sanitize
 * @returns Sanitized token or empty string if unsafe
 */
export const sanitizeRouteToken = (token: string): string => {
  if (!token || typeof token !== 'string') {
    return '';
  }

  const trimmed = token.trim();
  const safeTokenPattern = /^[a-zA-Z0-9._=-]+$/;

  return safeTokenPattern.test(trimmed) ? trimmed : '';
};

/**
 * Sanitizes file names for safe display
 * @param fileName - The file name to sanitize
 * @returns Sanitized file name
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName || typeof fileName !== 'string') {
    return '';
  }

  // First, remove any content within angle brackets (potential HTML tags or script injection)
  let cleanName = fileName.replace(/<[^>]*>/g, '');
  // Then, remove any other invalid filename characters
  cleanName = cleanName.replace(/[<>:"/\\|?*]/g, '').trim();
  return sanitizeText(cleanName);
};
